/**
 * Architecture Memory Page (MA-ARCH-046)
 * 
 * SuperAdmin-only knowledge repository for MissionAssist institutional knowledge.
 * Supports CRUD operations on architecture documentation.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Search, 
  Plus, 
  FileText, 
  Edit, 
  Trash2, 
  Book, 
  Cpu,
  GitBranch,
  AlertCircle,
  FileCode,
  RefreshCw,
  Filter,
  X
} from 'lucide-react';

const API = window.location.origin;

// Document type icons
const DOC_TYPE_ICONS = {
  architecture: Cpu,
  module: FileCode,
  process: GitBranch,
  decision_record: Book,
  troubleshooting: AlertCircle,
};

// Document type colors
const DOC_TYPE_COLORS = {
  architecture: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  module: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  process: 'bg-green-500/10 text-green-400 border-green-500/20',
  decision_record: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  troubleshooting: 'bg-red-500/10 text-red-400 border-red-500/20',
};

// Status colors
const STATUS_COLORS = {
  draft: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  published: 'bg-green-500/10 text-green-400 border-green-500/20',
  archived: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
};

export default function ArchitectureMemoryPage() {
  const navigate = useNavigate();
  const { isSuperAdmin } = useTenant();
  
  // State
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metadata, setMetadata] = useState({ modules: [], doc_types: [], statuses: [] });
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDocType, setFilterDocType] = useState('all');
  const [filterModule, setFilterModule] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Dialog state
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    doc_type: 'architecture',
    module: '',
    title: '',
    content: '',
    architecture_decision_id: '',
    status: 'draft',
  });

  // Redirect if not superadmin
  useEffect(() => {
    if (!isSuperAdmin) {
      toast.error('SuperAdmin access required');
      navigate('/');
    }
  }, [isSuperAdmin, navigate]);

  // Fetch metadata (modules, types, statuses)
  const fetchMetadata = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/architecture-memory/modules`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMetadata(data);
      }
    } catch (err) {
      console.error('Failed to fetch metadata:', err);
    }
  }, []);

  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (searchQuery) params.append('search', searchQuery);
      if (filterDocType && filterDocType !== 'all') params.append('doc_type', filterDocType);
      if (filterModule && filterModule !== 'all') params.append('module', filterModule);
      if (filterStatus && filterStatus !== 'all') params.append('status', filterStatus);
      
      const res = await fetch(`${API}/api/architecture-memory/documents?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (res.ok) {
        const data = await res.json();
        setDocuments(data);
      } else {
        toast.error('Failed to load documents');
      }
    } catch (err) {
      toast.error('Failed to load documents');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filterDocType, filterModule, filterStatus]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchMetadata();
      fetchDocuments();
    }
  }, [isSuperAdmin, fetchMetadata, fetchDocuments]);

  // Create document
  const handleCreate = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/architecture-memory/documents`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (res.ok) {
        toast.success('Document created');
        setIsCreateOpen(false);
        resetForm();
        fetchDocuments();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create document');
      }
    } catch (err) {
      toast.error('Failed to create document');
      console.error(err);
    }
  };

  // Update document
  const handleUpdate = async () => {
    if (!selectedDoc) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/architecture-memory/documents/${selectedDoc.doc_id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (res.ok) {
        toast.success('Document updated');
        setIsEditOpen(false);
        setSelectedDoc(null);
        resetForm();
        fetchDocuments();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update document');
      }
    } catch (err) {
      toast.error('Failed to update document');
      console.error(err);
    }
  };

  // Delete (archive) document
  const handleDelete = async (docId) => {
    if (!window.confirm('Archive this document?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/architecture-memory/documents/${docId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (res.ok) {
        toast.success('Document archived');
        fetchDocuments();
      } else {
        toast.error('Failed to archive document');
      }
    } catch (err) {
      toast.error('Failed to archive document');
      console.error(err);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      doc_type: 'architecture',
      module: '',
      title: '',
      content: '',
      architecture_decision_id: '',
      status: 'draft',
    });
  };

  // Open edit dialog
  const openEdit = (doc) => {
    setSelectedDoc(doc);
    setFormData({
      doc_type: doc.doc_type,
      module: doc.module || '',
      title: doc.title,
      content: doc.content,
      architecture_decision_id: doc.architecture_decision_id || '',
      status: doc.status,
    });
    setIsEditOpen(true);
  };

  // Open view dialog
  const openView = (doc) => {
    setSelectedDoc(doc);
    setIsViewOpen(true);
  };

  // Load template
  const loadTemplate = async () => {
    if (!formData.doc_type) return;
    
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        title: formData.title || 'Untitled',
        module: formData.module || '',
      });
      
      const res = await fetch(`${API}/api/architecture-memory/templates/${formData.doc_type}?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (res.ok) {
        const data = await res.json();
        setFormData({ ...formData, content: data.template });
        toast.success('Template loaded');
      } else {
        toast.error('Failed to load template');
      }
    } catch (err) {
      toast.error('Failed to load template');
      console.error(err);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setSearchQuery('');
    setFilterDocType('all');
    setFilterModule('all');
    setFilterStatus('all');
  };

  if (!isSuperAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Book className="h-6 w-6 text-blue-400" />
              Architecture Memory
            </h1>
            <p className="text-gray-400 mt-1">
              MissionAssist institutional knowledge repository
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchDocuments}
              className="border-gray-700"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button
              onClick={() => {
                resetForm();
                setIsCreateOpen(true);
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Document
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search documents..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-gray-800 border-gray-700 text-white"
                  />
                </div>
              </div>
              
              <Select value={filterDocType} onValueChange={setFilterDocType}>
                <SelectTrigger className="w-[160px] bg-gray-800 border-gray-700">
                  <SelectValue placeholder="Doc Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {metadata.doc_types.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type.replace('_', ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filterModule} onValueChange={setFilterModule}>
                <SelectTrigger className="w-[160px] bg-gray-800 border-gray-700">
                  <SelectValue placeholder="Module" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Modules</SelectItem>
                  {metadata.modules.map((mod) => (
                    <SelectItem key={mod} value={mod}>
                      {mod.replace('_', ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[140px] bg-gray-800 border-gray-700">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {metadata.statuses.map((status) => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {(searchQuery || filterDocType !== 'all' || filterModule !== 'all' || filterStatus !== 'all') && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Documents Table */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <FileText className="h-5 w-5 text-gray-400" />
              Documents ({documents.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-gray-400">Loading...</div>
            ) : documents.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                No documents found. Create your first architecture document.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-800">
                    <TableHead className="text-gray-400">Title</TableHead>
                    <TableHead className="text-gray-400">Type</TableHead>
                    <TableHead className="text-gray-400">Module</TableHead>
                    <TableHead className="text-gray-400">Status</TableHead>
                    <TableHead className="text-gray-400">Version</TableHead>
                    <TableHead className="text-gray-400">Updated</TableHead>
                    <TableHead className="text-gray-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documents.map((doc) => {
                    const TypeIcon = DOC_TYPE_ICONS[doc.doc_type] || FileText;
                    return (
                      <TableRow 
                        key={doc.doc_id} 
                        className="border-gray-800 hover:bg-gray-800/50 cursor-pointer"
                        onClick={() => openView(doc)}
                      >
                        <TableCell className="font-medium text-white">
                          <div className="flex items-center gap-2">
                            <TypeIcon className="h-4 w-4 text-gray-400" />
                            <span className="truncate max-w-[300px]">{doc.title}</span>
                            {doc.architecture_decision_id && (
                              <Badge variant="outline" className="text-xs border-gray-600">
                                {doc.architecture_decision_id}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={DOC_TYPE_COLORS[doc.doc_type]}>
                            {doc.doc_type.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-gray-300">
                          {doc.module || '-'}
                        </TableCell>
                        <TableCell>
                          <Badge className={STATUS_COLORS[doc.status]}>
                            {doc.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-gray-400">
                          v{doc.version}
                        </TableCell>
                        <TableCell className="text-gray-400 text-sm">
                          {new Date(doc.updated_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEdit(doc)}
                              className="text-gray-400 hover:text-white"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(doc.doc_id)}
                              className="text-gray-400 hover:text-red-400"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Development Rule Notice */}
        <Card className="bg-amber-950/20 border-amber-800/30">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-400 mt-0.5" />
              <div>
                <p className="text-amber-200 font-medium">Development Rule</p>
                <p className="text-amber-300/70 text-sm mt-1">
                  "No feature is considered complete until a corresponding Architecture Memory document is created or updated."
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Document</DialogTitle>
            <DialogDescription className="text-gray-400">
              Add architecture documentation to the knowledge repository.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Document Type</label>
                <Select
                  value={formData.doc_type}
                  onValueChange={(v) => setFormData({ ...formData, doc_type: v })}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {metadata.doc_types.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type.replace('_', ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Module</label>
                <Select
                  value={formData.module}
                  onValueChange={(v) => setFormData({ ...formData, module: v })}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700">
                    <SelectValue placeholder="Select module" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {metadata.modules.map((mod) => (
                      <SelectItem key={mod} value={mod}>
                        {mod.replace('_', ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Title</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Document title"
                className="bg-gray-800 border-gray-700"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">ADR ID (optional)</label>
                <Input
                  value={formData.architecture_decision_id}
                  onChange={(e) => setFormData({ ...formData, architecture_decision_id: e.target.value })}
                  placeholder="e.g., ADR-001"
                  className="bg-gray-800 border-gray-700"
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Status</label>
                <Select
                  value={formData.status}
                  onValueChange={(v) => setFormData({ ...formData, status: v })}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {metadata.statuses.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm text-gray-400">Content (Markdown supported)</label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={loadTemplate}
                  className="text-blue-400 hover:text-blue-300 h-7 text-xs"
                >
                  <FileText className="h-3 w-3 mr-1" />
                  Load Template
                </Button>
              </div>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Document content..."
                className="bg-gray-800 border-gray-700 min-h-[200px] font-mono text-sm"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)} className="border-gray-700">
              Cancel
            </Button>
            <Button onClick={handleCreate} className="bg-blue-600 hover:bg-blue-700">
              Create Document
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Document</DialogTitle>
            <DialogDescription className="text-gray-400">
              Update architecture documentation.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Document Type</label>
                <Select
                  value={formData.doc_type}
                  onValueChange={(v) => setFormData({ ...formData, doc_type: v })}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {metadata.doc_types.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type.replace('_', ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Module</label>
                <Select
                  value={formData.module}
                  onValueChange={(v) => setFormData({ ...formData, module: v })}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700">
                    <SelectValue placeholder="Select module" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {metadata.modules.map((mod) => (
                      <SelectItem key={mod} value={mod}>
                        {mod.replace('_', ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Title</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Document title"
                className="bg-gray-800 border-gray-700"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">ADR ID (optional)</label>
                <Input
                  value={formData.architecture_decision_id}
                  onChange={(e) => setFormData({ ...formData, architecture_decision_id: e.target.value })}
                  placeholder="e.g., ADR-001"
                  className="bg-gray-800 border-gray-700"
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Status</label>
                <Select
                  value={formData.status}
                  onValueChange={(v) => setFormData({ ...formData, status: v })}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {metadata.statuses.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Content (Markdown supported)</label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Document content..."
                className="bg-gray-800 border-gray-700 min-h-[200px] font-mono text-sm"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)} className="border-gray-700">
              Cancel
            </Button>
            <Button onClick={handleUpdate} className="bg-blue-600 hover:bg-blue-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedDoc && (
                <>
                  {(() => {
                    const TypeIcon = DOC_TYPE_ICONS[selectedDoc.doc_type] || FileText;
                    return <TypeIcon className="h-5 w-5 text-gray-400" />;
                  })()}
                  {selectedDoc.title}
                </>
              )}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {selectedDoc && (
                <div className="flex items-center gap-2 mt-2">
                  <Badge className={DOC_TYPE_COLORS[selectedDoc.doc_type]}>
                    {selectedDoc.doc_type.replace('_', ' ')}
                  </Badge>
                  {selectedDoc.module && (
                    <Badge variant="outline" className="border-gray-600">
                      {selectedDoc.module}
                    </Badge>
                  )}
                  <Badge className={STATUS_COLORS[selectedDoc.status]}>
                    {selectedDoc.status}
                  </Badge>
                  <span className="text-xs text-gray-500">
                    v{selectedDoc.version} • Updated {new Date(selectedDoc.updated_at).toLocaleDateString()}
                  </span>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {selectedDoc && (
            <div className="py-4">
              <div className="bg-gray-800 rounded-lg p-4 font-mono text-sm whitespace-pre-wrap text-gray-300 max-h-[500px] overflow-y-auto">
                {selectedDoc.content}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewOpen(false)} className="border-gray-700">
              Close
            </Button>
            <Button 
              onClick={() => {
                setIsViewOpen(false);
                openEdit(selectedDoc);
              }} 
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
